import React, { useEffect, useLayoutEffect, useRef } from "react";

import fundoUrl from "./chrome/bg-mapa.webp";
// SVG inline: o CRA usa @svgr/webpack com `-svgo,+ref`, ou seja, SVGO
// DESLIGADO e ref repassado — exatamente o que este componente precisa (os ids
// `regiao-*`/`pin-*` só são referenciados por CSS e JS, e um SVGO ativo os
// apagaria por "não usados"). No protótipo, em Vite, isso exigia configurar
// `cleanupIds: false` à mão.
import { ReactComponent as MapaSvg } from "./chrome/mapa-brasil.svg";

const CLASSE_ATIVA = "mapa__regiao--ativa";
const CLASSE_PIN_ATIVO = "mapa__pin--ativo";
const CLASSE_ROTULO_ATIVO = "mapa__rotulo--ativo";

// viewBox original do arquivo — fixo, não lido de `svg.viewBox.baseVal`, que é
// mutado em tempo de execução pelo recorte responsivo. Em StrictMode o efeito
// de montagem roda 2x no mesmo nó DOM, e na 2ª passagem ler o atributo
// devolveria o recorte da 1ª, encolhendo recursivamente.
const VIEWBOX_ORIGINAL = { width: 1920, height: 1080.22 };
// Folga ao redor da região no zoom. 1,6 = ~60% de respiro: a arte do fundo vale
// ser vista, e menos zoom também é menos ampliação do raster (menos borrão).
const MARGEM_ZOOM = 1.6;
const TRANSFORM_REPOUSO = "translate(0px, 0px) scale(1)";
// Folga ao redor da UNIÃO das 5 regiões no recorte responsivo — mais apertada
// que MARGEM_ZOOM porque aqui só se corta excesso de oceano nas pontas.
const MARGEM_BRASIL_INTEIRO = 1.25;

/**
 * viewBox mais estreito (só a largura; altura sempre a original) para reduzir o
 * letterbox em telas retrato. `larguraMinima` é um piso: nunca corta mais
 * apertado que a união das 5 regiões, então nenhuma região fica de fora.
 */
export function calcularViewBoxResponsivo(
  larguraContainer,
  alturaContainer,
  viewBoxOriginal,
  caixaBrasilInteiro
) {
  if (!larguraContainer || !alturaContainer || !caixaBrasilInteiro) {
    return viewBoxOriginal;
  }

  const aspectContainer = larguraContainer / alturaContainer;
  const alturaFixa = viewBoxOriginal.height;
  const larguraIdeal = aspectContainer * alturaFixa;
  const larguraMinima = caixaBrasilInteiro.largura * MARGEM_BRASIL_INTEIRO;
  const larguraFinal = Math.min(
    Math.max(larguraIdeal, larguraMinima),
    viewBoxOriginal.width
  );

  const centroX = caixaBrasilInteiro.x + caixaBrasilInteiro.largura / 2;
  let x = centroX - larguraFinal / 2;
  x = Math.max(0, Math.min(x, viewBoxOriginal.width - larguraFinal));

  return { x, y: 0, width: larguraFinal, height: alturaFixa };
}

/** Menor retângulo que contém todas as caixas ao mesmo tempo. */
export function uniaoDeCaixas(caixas) {
  const lista = Object.values(caixas || {});
  if (lista.length === 0) return null;
  const minX = Math.min(...lista.map(c => c.x));
  const minY = Math.min(...lista.map(c => c.y));
  const maxX = Math.max(...lista.map(c => c.x + c.largura));
  const maxY = Math.max(...lista.map(c => c.y + c.altura));
  return { x: minX, y: minY, largura: maxX - minX, altura: maxY - minY };
}

/**
 * Mede a caixa de uma região em coordenadas do viewBox, usando a matriz do
 * próprio SVG (getScreenCTM) em vez de recalcular escala e letterbox à mão —
 * era o cálculo manual que estava errado quando o mapa era <img>.
 *
 * Devolve null quando o ambiente não tem essas APIs (jsdom nos testes).
 */
export function medirRegiaoNoViewBox(svg, regiaoId) {
  const alvo = svg.querySelector(`#regiao-${regiaoId}`);
  const ctm = svg.getScreenCTM && svg.getScreenCTM();
  if (!alvo || !ctm || !svg.createSVGPoint) return null;

  const inversa = ctm.inverse();
  const paraViewBox = (x, y) => {
    const ponto = svg.createSVGPoint();
    ponto.x = x;
    ponto.y = y;
    return ponto.matrixTransform(inversa);
  };

  const caixaNaTela = alvo.getBoundingClientRect();
  if (caixaNaTela.width === 0 || caixaNaTela.height === 0) return null;

  const inicio = paraViewBox(caixaNaTela.left, caixaNaTela.top);
  const fim = paraViewBox(caixaNaTela.right, caixaNaTela.bottom);
  return {
    x: inicio.x,
    y: inicio.y,
    largura: fim.x - inicio.x,
    altura: fim.y - inicio.y
  };
}

/**
 * Transform que centraliza `caixa` no viewBox e amplia até preenchê-la, com a
 * folga pedida — tudo em unidades do viewBox, logo independente do tamanho e da
 * proporção da tela, sem listener de resize.
 */
export function transformParaCaixa(caixa, viewBox, margem) {
  const escala = Math.min(
    viewBox.width / (caixa.largura * margem),
    viewBox.height / (caixa.altura * margem)
  );
  const centroX = caixa.x + caixa.largura / 2;
  const centroY = caixa.y + caixa.altura / 2;
  const tx = viewBox.x + viewBox.width / 2 - escala * centroX;
  const ty = viewBox.y + viewBox.height / 2 - escala * centroY;
  return `translate(${tx}px, ${ty}px) scale(${escala})`;
}

/**
 * Mede TODAS as regiões com a geometria neutra e devolve o mapa id → caixa.
 *
 * Neutraliza o transform antes de medir porque a medição lê a caixa RENDERIZADA:
 * um transform já aplicado contaminaria o resultado (o zoom sairia calculado a
 * partir de um tamanho que já inclui zoom). Em StrictMode o efeito roda duas
 * vezes e na segunda o transform da primeira ainda está lá — daí o reset
 * explícito. A transição é desligada durante a manobra para o mapa não animar
 * até o repouso, e restaurada no fim.
 *
 * Devolve `{}` quando o ambiente não permite medir (jsdom, ou caixa de tamanho
 * zero porque o mapa ainda não tem área na tela).
 */
function medirRegioes(svg, conteudo) {
  const transicaoAnterior = conteudo.style.transition;
  const transformAnterior = conteudo.style.transform;

  conteudo.style.transition = "none";
  conteudo.style.transform = "none";
  conteudo.getBoundingClientRect(); // força o reflow antes de medir

  const caixas = {};
  svg.querySelectorAll('[id^="regiao-"]').forEach(el => {
    const caixa = medirRegiaoNoViewBox(svg, el.id.replace("regiao-", ""));
    if (caixa) caixas[el.id.replace("regiao-", "")] = caixa;
  });

  conteudo.style.transform = transformAnterior;
  conteudo.getBoundingClientRect(); // idem, antes de religar a transição
  conteudo.style.transition = transicaoAnterior;

  return caixas;
}

/**
 * Mapa do Brasil com uma das 5 regiões destacada.
 *
 * O destaque NÃO está pintado no arquivo: aqui se marca o path da região com
 * uma classe e o CSS cuida do visual — é o que permite o mesmo arquivo do
 * ilustrador servir para qualquer região.
 *
 * @param regiaoId  precisa existir no SVG como `#regiao-{regiaoId}`
 * @param etapa     'cinza' | 'contorno' (só o traço acende) | 'destaque'
 * @param zoom      quando true, aproxima na região destacada
 */
function MapaBrasil({ regiaoId, etapa = "cinza", zoom = false }) {
  const svgRef = useRef(null);
  const caixasRef = useRef(null);

  useEffect(() => {
    const svg = svgRef.current;
    const alvo = svg && svg.querySelector(`#regiao-${regiaoId}`);
    if (!alvo) {
      // Falhar em silêncio seria pior: a região nunca acenderia e a abertura
      // pareceria "quebrada sem erro nenhum".
      // eslint-disable-next-line no-console
      console.warn(
        `MapaBrasil: região "${regiaoId}" não encontrada no SVG do mapa.`
      );
      return undefined;
    }
    const pin = svg.querySelector(`#pin-${regiaoId}`);
    const rotulo = svg.querySelector(`#rotulo-${regiaoId}`);
    alvo.classList.add(CLASSE_ATIVA);
    if (pin) pin.classList.add(CLASSE_PIN_ATIVO);
    if (rotulo) rotulo.classList.add(CLASSE_ROTULO_ATIVO);
    return () => {
      alvo.classList.remove(CLASSE_ATIVA);
      if (pin) pin.classList.remove(CLASSE_PIN_ATIVO);
      if (rotulo) rotulo.classList.remove(CLASSE_ROTULO_ATIVO);
    };
  }, [regiaoId]);

  // Mede TODAS as regiões na montagem. Este efeito precisa vir ANTES do que
  // aplica o transform.
  //
  // A medição de montagem PODE FALHAR, e no AVA falha: a trilha nasce dentro de
  // um `tab-pane`, que não tem área na tela até a aba ativar, e caixa de tamanho
  // zero não dá para medir. Quem garante o zoom é a segunda tentativa no efeito
  // abaixo — aqui é só o caminho rápido, para o caso comum de o mapa já ter área.
  useLayoutEffect(() => {
    const svg = svgRef.current;
    const conteudo = svg && svg.querySelector("#mapa-conteudo");
    if (!svg || !conteudo) return undefined;

    conteudo.style.transition = "none";
    conteudo.style.transform = "none";
    conteudo.getBoundingClientRect(); // força o reflow antes de seguir

    // O raster do fundo é apontado aqui, não dentro do SVG: assim a URL passa
    // pelo bundler (com hash de cache) em vez de virar caminho literal.
    const fundo = svg.querySelector("#mapa-fundo");
    if (fundo) fundo.setAttribute("href", fundoUrl);

    caixasRef.current = medirRegioes(svg, conteudo);

    // O repouso vale já no primeiro quadro, sem animar: com a transição ligada,
    // o mapa passaria os primeiros segundos se ajustando sozinho.
    conteudo.style.transform = TRANSFORM_REPOUSO;
    conteudo.getBoundingClientRect(); // força o reflow antes de seguir
    conteudo.style.transition = "";

    // O viewBox fica como veio do arquivo, e a moldura desenha exatamente essa
    // caixa. NÃO há recorte responsivo aqui (embora `calcularViewBoxResponsivo`
    // siga exportada e testada, para quem for usar o mapa em tela cheia).
    //
    // Por quê: aquele recorte existe para eliminar o letterbox de um mapa 16:9
    // esticado numa VIEWPORT de proporção qualquer. Aqui o mapa vive numa caixa
    // que já é 16:9 por construção (`aspect-ratio` no CSS da abertura), então
    // não há letterbox para eliminar — e o recálculo, medindo o container antes
    // de o `aspect-ratio` assentar, estreitava o viewBox e deixava o mapa
    // ampliado e fora de centro (visto ao vivo). Sem recorte, o
    // `preserveAspectRatio` encaixa a arte inteira, centralizada, em qualquer
    // largura.
    const moldura = svg.querySelector("#mapa-moldura");
    if (moldura) {
      moldura.setAttribute("x", 0);
      moldura.setAttribute("y", 0);
      moldura.setAttribute("width", VIEWBOX_ORIGINAL.width);
      moldura.setAttribute("height", VIEWBOX_ORIGINAL.height);
    }

    return undefined;
  }, []);

  useLayoutEffect(() => {
    const svg = svgRef.current;
    const conteudo = svg && svg.querySelector("#mapa-conteudo");
    if (!conteudo || !svg.viewBox) return;
    const viewBox = svg.viewBox.baseVal;
    let caixa = caixasRef.current && caixasRef.current[regiaoId];

    // SEGUNDA CHANCE, e é ela que faz o zoom acontecer no AVA. Quando o zoom é
    // pedido o mapa está na tela há segundos, então medir AGORA funciona mesmo
    // que a medição de montagem tenha falhado (medido no AVA: na montagem a
    // caixa é zero, e ~80ms depois a região já mede 82x99).
    //
    // Sem isto o zoom não acontecia e nada na tela dizia por quê — o mapa
    // simplesmente ficava parado, o que lia como "a animação não funciona".
    if (zoom && !caixa) {
      caixasRef.current = medirRegioes(svg, conteudo);
      caixa = caixasRef.current[regiaoId];
      if (!caixa) {
        // Só sobra ambiente sem `getScreenCTM` (jsdom) ou mapa realmente sem
        // área. Falhar em silêncio seria pior.
        // eslint-disable-next-line no-console
        console.warn(
          `MapaBrasil: região "${regiaoId}" não foi medida; o zoom não vai acontecer.`
        );
      }
    }

    conteudo.style.transform =
      zoom && caixa
        ? transformParaCaixa(caixa, viewBox, MARGEM_ZOOM)
        : TRANSFORM_REPOUSO;
  }, [zoom, regiaoId]);

  return (
    <MapaSvg
      ref={svgRef}
      className="mapa-brasil"
      data-tl-region={regiaoId}
      data-etapa={etapa}
      data-zoom={zoom ? "true" : "false"}
    />
  );
}

export default MapaBrasil;
